import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid-premium";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Stack,
} from "@mui/material";
import ByzMuiDtButton from "./custom-button";
import ByzMuiDtConfirmDialog from "../data-grid/custom-dialog";
import { useState } from "react";
import ByzMuiTypography from "../inputs/ByzMuiTypography";
import { useTranslate, useLocales } from "src/locales";
import { GridExperimentalPremiumFeatures } from "@mui/x-data-grid-premium";
import { getGridLocaleText } from "./gridLocaleText";
interface DataGridProps extends React.ComponentProps<typeof DataGrid> {
  onDelete?: (id: any) => void;
  onUpdate?: (id: any) => void;
  onNavigate?: (id: any) => void;
  hasDetailModal?: boolean;
  roles?: string[];
  pageSize?: number;
  fields?: any;
  columns: GridColDef[];
  customToolbar?: any;
  disableProcessMenu?: boolean;
  rowSelectionModel?: any;
  getDetailPanelHeight?: any;
  getDetailPanelContent?: any;
  getDetailPanelExpanded?: any;
  onDetailPanelExpandedChange?: any;
  sx?: any;
  columnGroupingModel?: GridColumnGroupingModel;
  experimentalFeatures?: Partial<GridExperimentalPremiumFeatures>;
  checkboxSelection?: boolean;
  enableRowZoom?: boolean;
  loading?: boolean;
  whiteGrid?: boolean;
  headerWhite?: boolean;
}

const ByzMuiDataGrid = (props: DataGridProps) => {
  const [modalDatas, setModalDatas] = useState<{ key: string; value: any }[]>(
    []
  );
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const { t } = useTranslate();
  const { currentLang } = useLocales();

  const handleOpen = (data: any) => {
    setOpen(true);
    const arr = Object.keys(data).map((key) => ({ key, value: data[key] }));
    setModalDatas(arr);
  };

  const {
    onDelete,
    onUpdate,
    onNavigate,
    roles,
    fields,
    rowSelectionModel,
    getDetailPanelHeight,
    getDetailPanelContent,
    hasDetailModal,
    customToolbar,
    disableProcessMenu,
    checkboxSelection,
    pageSize,
    sx,
    rows,
    columns: baseColumns,
    enableRowZoom,
    loading,
    whiteGrid,
    headerWhite = true,
    initialState: initialStateFromProps,
    ...muiProps
  } = props;

  const handleClose = () => setOpen(false);

  const handleDeleteClick = (id: any) => {
    setSelectedRowId(id);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setSelectedRowId(null);
  };

  const handleConfirmDelete = () => {
    if (props.onDelete && selectedRowId !== null) {
      props.onDelete(selectedRowId);
    }
    handleConfirmClose();
  };


  const columns = [...props.columns];

  if (props.hasDetailModal) {
    columns.push({
      field: "detail-2",
      headerName: "Detail - 2",
      width: 150,

      renderCell: (params) => (
        <ByzMuiDtButton
          variant="outlined"
          onClick={() => handleOpen(params.row)}
          color="info"
        >
          Details modal
        </ByzMuiDtButton>
      ),
    });
  }

  if (props.onNavigate) {
    columns.push({
      field: "navigate",
      headerName: "",
      width: 90,
      renderCell: (params) => (
        <Box
          sx={{ cursor: "pointer", width: "100%", textAlign: "center" }}
          onClick={() => props.onNavigate?.(params.row.id)}
        >
          <LaunchIcon />
        </Box>
      ),
    });
  }

  if (props.onUpdate) {
    columns.push({
      field: "update",
      headerName: "",
      width: 90,
      renderCell: (params) => (
        <Box
          sx={{ cursor: "pointer", width: "100%", textAlign: "center" }}
          onClick={() => props.onUpdate?.(params.row.id)}
        >
          <EditIcon />
        </Box>
      ),
    });
  }

  if (props.onDelete) {
    columns.push({
      field: "delete",
      headerName: "",
      width: 80,
      renderCell: (params) => (
        <Box
          sx={{ cursor: "pointer", width: "100%", textAlign: "center" }}
          onClick={() => handleDeleteClick(params.row.id)}
        >
          <DeleteIcon color="error" />
        </Box>
      ),
    });
  }

  const mergedInitialState =
    pageSize === undefined
      ? initialStateFromProps
      : {
          ...initialStateFromProps,
          pagination: {
            ...initialStateFromProps?.pagination,
            paginationModel: {
              ...initialStateFromProps?.pagination?.paginationModel,
              pageSize,
            },
          },
        };

  const CustomNoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        padding: "20px",
      }}
    >
      <ByzMuiTypography variant="body1">
        {t("no-data-found")}
      </ByzMuiTypography>
    </Box>
  );

  return (
    <Stack sx={{ ...props.sx, marginTop: "5px" }}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            maxWidth: 520,
            width: "100%",
            height: "auto",
            p: 2, // Add padding to the dialog
          },
        }}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <DialogTitle sx={{ pb: 2 }}>Detaylar</DialogTitle>

        {modalDatas && (
          <DialogContent sx={{ typography: "body1", p: 3 }}>
            {modalDatas.length > 0 ? (
              modalDatas.map((data) => (
                <ByzMuiTypography
                  id="modal-description"
                  variant="body1"
                  key={data.key}
                  sx={{ mb: 1 }}
                >
                  {data.key}: {data.value}
                </ByzMuiTypography>
              ))
            ) : (
              <ByzMuiTypography sx={{ mt: 2 }}>
                Gösterilecek kayıt bulunamadı.
              </ByzMuiTypography>
            )}
          </DialogContent>
        )}

        <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            {t("buttons.close", "uppercase")}
          </Button>
        </DialogActions>
      </Dialog>

      <ByzMuiDtConfirmDialog
        title="Silme İşlemi"
        content="Seçilen datayı silmek istediğinize emin misiniz?"
        open={confirmOpen}
        onClose={handleConfirmClose}
        action={
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDelete}
          >
            Sil
          </Button>
        }
      />

      <DataGrid
        slots={{
          toolbar: props.customToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
          noResultsOverlay: CustomNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            printOptions: { disableToolbarButton: true },
          },
          baseButton: {
            size: "medium",
            variant: "text",
          },
          // @ts-ignore
          toolbarColumnsButton: {
            startIcon: (
              <Box
                component="img"
                src="/assets/icons/grid/ViewColumnFilled.svg"
                sx={{ width: 16, height: 18 }}
                alt=""
              />
            ),
          },
          // @ts-ignore
          toolbarFilterButton: {
            startIcon: (
              <Box
                component="img"
                src="/assets/icons/grid/FilterListFilled.svg"
                sx={{ width: 16, height: 18 }}
                alt=""
              />
            ),
          },
          // @ts-ignore
          toolbarDensitySelector: {
            startIcon: (
              <Box
                component="img"
                src="/assets/icons/grid/TableRowsFilled.svg"
                sx={{ width: 16, height: 18 }}
                alt=""
              />
            ),
          },
          // @ts-ignore
          toolbarExport: {
            startIcon: (
              <Box
                component="img"
                src="/assets/icons/grid/SaveAltFilled.svg"
                sx={{ width: 16, height: 18 }}
                alt=""
              />
            ),
          },
        }}
        localeText={getGridLocaleText(t, currentLang.value) as any}
        pageSizeOptions={[5, 10, 20]}
        rows={props.rows}
        columns={columns}
        rowSelectionModel={rowSelectionModel}
        {...(mergedInitialState != null ? { initialState: mergedInitialState } : {})}
        pagination
        disableColumnMenu={props.disableProcessMenu}
        getDetailPanelContent={props.getDetailPanelContent}
        getDetailPanelHeight={props.getDetailPanelHeight}
        columnGroupingModel={props.columnGroupingModel}
        experimentalFeatures={props.experimentalFeatures}
        checkboxSelection={props.checkboxSelection} //YATAY;443935;02042025
        loading={loading}
        sx={{
          flexGrow: 1,
          minHeight: props.rows?.length === 0 ? 400 : "unset",
          ...props.sx,
          ...(enableRowZoom && {
            "& .MuiDataGrid-row": {
              transition: "all 0.1s ease",
              "&:hover": {
                backgroundColor: "#f5f5f5 !important",
                "& .MuiDataGrid-cell": {
                  fontSize: "1em",
                  fontWeight: "600",
                  color: "#1976d2",
                  padding: "8px 16px",
                  "& .MuiCheckbox-root": {
                    transform: "scale(1)",
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.5rem",
                    },
                  },
                  "& .MuiButton-root": {
                    transform: "scale(1.2)",
                    fontSize: "1.2em",
                  },
                  "& .MuiIconButton-root": {
                    transform: "scale(1.2)",
                  },
                },
              },
            },
            "& .MuiDataGrid-cell": {
              transition: "all 0.05s ease-in-out",
            },
          }),
          "& .MuiDataGrid-toolbarContainer": {
            "& .MuiButton-root": {
              position: "relative",
              borderRadius: "4px",
              overflow: "hidden",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px 5px",
              boxSizing: "border-box",
              textAlign: "left",
              fontSize: "13px",
              color: "#2196f3",
              fontFamily: "Poppins",
              letterSpacing: "0.46px",
              lineHeight: "22px",
              textTransform: "uppercase",
              fontWeight: 500,
              gap: "8px",
              "&:hover": {
                backgroundColor: "rgba(48, 156, 243, 0.1)",
              },
              "& .MuiButton-startIcon": {
                margin: 0,
                "& > *:first-of-type": {
                  fontSize: "18px",
                  width: "16px",
                  height: "18px",
                },
              },
            },
            "& .MuiTypography-root": {
              color: "#2196f3", // Updated color
            },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "& > div:first-of-type": {
              display: "flex",
              gap: 1,
            },
          },
          ...(whiteGrid && {
            border: "none !important",
            "& .MuiDataGrid-root": {
              border: "none !important",
            },
            "& .MuiDataGrid-main": {
              border: "none !important",
            },
            "& .MuiDataGrid-row": {
              borderBottom: "1px solid #e5e5e5 !important",
              "&:last-of-type": {
                borderBottom: "none !important",
                "& .MuiDataGrid-cell": {
                  borderBottom: "none !important",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e5e5e5 !important",
              display: "flex",
              alignItems: "center",
              "&:last-of-type": {
                borderRight: "none !important",
              },
            },
            "& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
              borderBottom: "none !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid #555 !important",
              backgroundColor: "white !important",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "white !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#333 !important",
              fontWeight: "600 !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              minHeight: "unset !important",
              border: "none !important",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none !important",
              border: "none !important",
            },

            "& .MuiDataGrid-footerContainer .MuiTablePagination-root": {
              margin: "0 !important",
              padding: "0 !important",
            },
          }),
          ...(headerWhite && {
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F4F7FE !important",
              border: "1px solid #E0E0E0",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#F4F7FE !important",
              borderRight: "1px solid #E0E0E0",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "rgba(0, 0, 0, 0.87)",
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: "Poppins",
              letterSpacing: "0.17px",
              lineHeight: "24px",
            },
            "& .MuiDataGrid-cell": {
              border: "1px solid #E2E8F0",
            },
          }),
        }}
        {...muiProps}
      />

    </Stack>
  );
};

export default ByzMuiDataGrid;
